import Keycloak from 'keycloak-js';
import { isFeatureEnabled } from 'saur-viseau-state-management/lib/settings/selectors';
import { store } from '../configStore';

/** Retourne true uniquement si le feature flag 'Show-Token-Debug' est activé dans le store Redux */
const isTokenDebugEnabled = (): boolean => {
    try {
        return isFeatureEnabled(store.getState(), 'Show-Token-Debug');
    } catch {
        return false;
    }
};

let authProvider: Keycloak | undefined = undefined;

export type TokensType = {
    idToken: string;
    refreshToken: string;
    token: string;
};

const initKeycloakProvider = (): Keycloak => {
    return new Keycloak(
        JSON.parse(
            localStorage.getItem(providerLocalstorageKey) ??
                JSON.stringify({
                    url: process.env.REACT_APP_URL_KEYCLOAK_AUTH,
                    realm: process.env.REACT_APP_KEYCLOAK_REALM,
                    clientId: process.env.REACT_APP_KEYCLOAK_CPO_CLIENT_ID
                    // possible os : REACT_APP_KEYCLOAK_CPO_CLIENT_ID_INTERNE | REACT_APP_KEYCLOAK_CPO_CLIENT_ID
                })
        )
    );
};

export const getAuthProvider = (): Keycloak => {
    // eslint-disable-next-line no-undef
    if (typeof authProvider === 'undefined') {
        authProvider = initKeycloakProvider();
    }
    return authProvider;
};

/**
 * Rafraîchit le token d'accès via l'endpoint Keycloak en utilisant le refresh_token
 * stocké dans localStorage['sso-response'].
 * Met à jour le localStorage avec les nouveaux tokens si le rafraîchissement réussit.
 * @returns Les nouveaux tokens ou null si le rafraîchissement a échoué.
 */
export const refreshAccessToken = async (): Promise<TokensType | null> => {
    try {
        const ssoResponse = JSON.parse(localStorage.getItem('sso-response') ?? 'null');
        const providerConfig = JSON.parse(localStorage.getItem(providerLocalstorageKey) ?? 'null');

        const refreshToken = ssoResponse?.refresh_token;
        const url = providerConfig?.url ?? process.env.REACT_APP_URL_KEYCLOAK_AUTH;
        const realm = providerConfig?.realm ?? process.env.REACT_APP_KEYCLOAK_REALM;
        const clientId = providerConfig?.clientId ?? process.env.REACT_APP_KEYCLOAK_CPO_CLIENT_ID;

        if (!refreshToken || !url || !realm || !clientId) {
            if (isTokenDebugEnabled()) {
                console.warn('[refreshAccessToken] Config incomplète — impossible de rafraîchir', {
                    hasRefreshToken: !!refreshToken,
                    url,
                    realm,
                    clientId,
                });
            }
            return null;
        }

        const tokenEndpoint = `${url}/realms/${realm}/protocol/openid-connect/token`;
        if (isTokenDebugEnabled()) {
            console.log('[refreshAccessToken] ⟳ Tentative de refresh…', {
                tokenEndpoint,
                refreshToken: `${refreshToken.slice(0, 20)}…`,
                currentExpiresAt: ssoResponse?.expires_at
                    ? new Date(ssoResponse.expires_at * 1000).toLocaleTimeString('fr-FR')
                    : 'N/A',
            });
        }

        const response = await fetch(tokenEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: new URLSearchParams({
                grant_type: 'refresh_token',
                client_id: clientId,
                refresh_token: refreshToken,
            }).toString(),
        });

        if (!response.ok) {
            if (isTokenDebugEnabled()) {
                console.error('[refreshAccessToken] ❌ Échec HTTP :', response.status, response.statusText,
                    '→ Le refresh_token est probablement expiré ou la session Keycloak a été invalidée.'
                );
            }
            return null;
        }

        const data = await response.json();

        // Mise à jour du localStorage avec les nouveaux tokens
        const updatedSsoResponse = {
            ...ssoResponse,
            access_token: data.access_token,
            refresh_token: data.refresh_token,
            id_token: data.id_token,
            expires_at: Math.floor(Date.now() / 1000) + data.expires_in,
        };
        localStorage.setItem('sso-response', JSON.stringify(updatedSsoResponse));

        if (isTokenDebugEnabled()) {
            console.log('[refreshAccessToken] ✅ Refresh réussi', {
                access_token: `${data.access_token?.slice(0, 20)}…`,
                id_token: `${data.id_token?.slice(0, 20)}…`,
                expires_in: `${data.expires_in}s`,
                new_expires_at: new Date(updatedSsoResponse.expires_at * 1000).toLocaleTimeString('fr-FR'),
            });
        }

        return {
            token: data.access_token,
            refreshToken: data.refresh_token,
            idToken: data.id_token,
        };
    } catch (error) {
        if (isTokenDebugEnabled()) console.error('[refreshAccessToken] ❌ Exception inattendue :', error);
        return null;
    }
};

export interface KeyCloakProvider {
    url: string;
    realm: string;
    clientId: string;
}

export const providerLocalstorageKey = 'provider';
